import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Card,
    CardContent,
    LinearProgress,
    Paper,
    Stack,
    Tabs,
    Tab,
    Typography,
} from '@mui/material';
import { Title, useDataProvider, useNotify } from 'react-admin';

import {
    BILLING_COUNTRY_CURRENCIES_RESOURCE,
    BILLING_FEATURES_RESOURCE,
    BILLING_PLAN_FEATURES_RESOURCE,
    BILLING_PLANS_RESOURCE,
    type BillingCountryCurrency,
    type BillingFeature,
    type BillingPlan,
    type BillingPlanFeature,
} from './billingData';
import { BillingCountryCurrenciesSection } from './BillingCountryCurrenciesSection';
import { BillingEntitlementsSection } from './BillingEntitlementsSection';
import { BillingFeaturesSection } from './BillingFeaturesSection';
import { BillingPlansSection } from './BillingPlansSection';
import { UserSubscriptionActivationSection } from './UserSubscriptionActivationSection';
import { getPlanPriceCount } from './billingUtils';

type BillingSection = 'features' | 'plans' | 'entitlements' | 'country-currencies' | 'user-activation';

const billingSections: BillingSection[] = ['features', 'plans', 'entitlements', 'country-currencies', 'user-activation'];

function getActiveSection(pathname: string): BillingSection {
    if (pathname.includes('/billing/pricing')) return 'entitlements';
    if (pathname.includes('/billing/countries')) return 'country-currencies';
    const match = pathname.split('/').find((segment): segment is BillingSection =>
        billingSections.includes(segment as BillingSection),
    );
    return match ?? 'features';
}

function SummaryCard({
    label,
    value,
    caption,
}: {
    label: string;
    value: string;
    caption: string;
}) {
    return (
        <Paper variant="outlined" sx={{ p: 2, minWidth: 180 }}>
            <Stack spacing={0.5}>
                <Typography variant="overline" color="text.secondary">
                    {label}
                </Typography>
                <Typography variant="h5">{value}</Typography>
                <Typography variant="body2" color="text.secondary">
                    {caption}
                </Typography>
            </Stack>
        </Paper>
    );
}

export function BillingPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const notify = useNotify();
    const dataProvider = useDataProvider();

    const activeSection = getActiveSection(location.pathname);

    const [features, setFeatures] = useState<BillingFeature[]>([]);
    const [plans, setPlans] = useState<BillingPlan[]>([]);
    const [planFeatures, setPlanFeatures] = useState<BillingPlanFeature[]>([]);
    const [countryCurrencies, setCountryCurrencies] = useState<BillingCountryCurrency[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const loadSequenceRef = useRef(0);

    const activePlans = useMemo(() => plans.filter((item) => item.isActive), [plans]);
    const totalAssignedFeatures = useMemo(() => planFeatures.length, [planFeatures]);
    const airwallexCountryCurrencies = useMemo(
        () => countryCurrencies.filter((countryCurrency) => countryCurrency.isAirwallexSupported),
        [countryCurrencies],
    );
    const totalRegionalPrices = useMemo(
        () => plans.reduce((total, plan) => total + getPlanPriceCount(plan, airwallexCountryCurrencies), 0),
        [airwallexCountryCurrencies, plans],
    );
    const airwallexCountries = useMemo(
        () => countryCurrencies.filter((countryCurrency) => countryCurrency.isAirwallexSupported).length,
        [countryCurrencies],
    );

    const loadFeatures = useCallback(async () => {
        const result = await dataProvider.getList(BILLING_FEATURES_RESOURCE, {
            pagination: { page: 1, perPage: 250 },
            sort: { field: 'name', order: 'ASC' },
            filter: {},
        });
        setFeatures(result.data as BillingFeature[]);
        return result;
    }, [dataProvider]);

    const loadPlans = useCallback(async () => {
        const result = await dataProvider.getList(BILLING_PLANS_RESOURCE, {
            pagination: { page: 1, perPage: 250 },
            sort: { field: 'name', order: 'ASC' },
            filter: {},
        });
        setPlans(result.data as BillingPlan[]);
        return result;
    }, [dataProvider]);

    const loadPlanFeatures = useCallback(async () => {
        const result = await dataProvider.getList(BILLING_PLAN_FEATURES_RESOURCE, {
            pagination: { page: 1, perPage: 250 },
            sort: { field: 'id', order: 'ASC' },
            filter: {},
        });
        setPlanFeatures(result.data as BillingPlanFeature[]);
        return result;
    }, [dataProvider]);

    const loadCountryCurrencies = useCallback(async () => {
        const result = await dataProvider.getList(BILLING_COUNTRY_CURRENCIES_RESOURCE, {
            pagination: { page: 1, perPage: 1000 },
            sort: { field: 'countryName', order: 'ASC' },
            filter: {},
        });
        setCountryCurrencies(result.data as BillingCountryCurrency[]);
        return result;
    }, [dataProvider]);

    const loadBillingData = useCallback(async () => {
        const loadSequence = loadSequenceRef.current + 1;
        loadSequenceRef.current = loadSequence;
        setIsLoading(true);

        try {
            if (activeSection === 'features') {
                await loadFeatures();
                return;
            }

            if (activeSection === 'plans') {
                await Promise.all([loadPlans(), loadCountryCurrencies()]);
                return;
            }

            if (activeSection === 'entitlements') {
                await Promise.all([loadFeatures(), loadPlans(), loadPlanFeatures(), loadCountryCurrencies()]);
                return;
            }

            if (activeSection === 'country-currencies') {
                await loadCountryCurrencies();
                return;
            }

            if (activeSection === 'user-activation') {
                await Promise.all([loadPlans(), loadCountryCurrencies()]);
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to load billing data';
            notify(message, { type: 'error' });
        } finally {
            if (loadSequenceRef.current === loadSequence) {
                setIsLoading(false);
            }
        }
    }, [activeSection, loadCountryCurrencies, loadFeatures, loadPlanFeatures, loadPlans, notify]);

    useEffect(() => {
        void loadBillingData();
    }, [loadBillingData]);

    return (
        <Card>
            <Title title="Subscriptions" />
            {isLoading ? <LinearProgress /> : null}
            <CardContent>
                <Stack spacing={3}>
                    <Stack spacing={1}>
                        <Typography variant="h4">Subscriptions & Billing</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage country-currency mappings, market prices, Airwallex availability, plans, and plan
                            entitlements in one admin workspace.
                        </Typography>
                    </Stack>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <SummaryCard
                            label="Features"
                            value={String(features.length)}
                            caption="Commerce capabilities that can be attached to a plan"
                        />
                        <SummaryCard
                            label="Plans"
                            value={String(plans.length)}
                            caption={`${activePlans.length} active plan${activePlans.length === 1 ? '' : 's'} available for sale`}
                        />
                        <SummaryCard
                            label="Regional Prices"
                            value={String(totalRegionalPrices)}
                            caption="Market-specific plan prices aligned to each country-currency mapping"
                        />
                        <SummaryCard
                            label="Country Currencies"
                            value={String(countryCurrencies.length)}
                            caption={`${airwallexCountries} market${airwallexCountries === 1 ? '' : 's'} currently support Airwallex checkout`}
                        />
                        <SummaryCard
                            label="Entitlements"
                            value={String(totalAssignedFeatures)}
                            caption="Plan-to-feature values aligned with plan-feature backend records"
                        />
                    </Stack>

                    <Tabs
                        value={activeSection}
                        onChange={(_event, nextValue: BillingSection) => navigate(`/billing/${nextValue}`)}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                    >
                        <Tab value="features" label="Features" />
                        <Tab value="plans" label="Plans" />
                        <Tab value="entitlements" label="Entitlements" />
                        <Tab value="country-currencies" label="Country Currencies" />
                        <Tab value="user-activation" label="User Activation" />
                    </Tabs>

                    {activeSection === 'features' ? (
                        <BillingFeaturesSection features={features} onChanged={loadBillingData} />
                    ) : null}

                    {activeSection === 'plans' ? (
                        <BillingPlansSection
                            countryCurrencies={airwallexCountryCurrencies}
                            plans={plans}
                            onChanged={loadBillingData}
                        />
                    ) : null}

                    {activeSection === 'entitlements' ? (
                        <BillingEntitlementsSection
                            countryCurrencies={airwallexCountryCurrencies}
                            features={features}
                            planFeatures={planFeatures}
                            plans={plans}
                            onChanged={loadBillingData}
                        />
                    ) : null}

                    {activeSection === 'country-currencies' ? (
                        <BillingCountryCurrenciesSection
                            countryCurrencies={countryCurrencies}
                            onChanged={loadBillingData}
                        />
                    ) : null}

                    {activeSection === 'user-activation' ? (
                        <UserSubscriptionActivationSection
                            countryCurrencies={airwallexCountryCurrencies}
                            plans={plans}
                        />
                    ) : null}
                </Stack>
            </CardContent>

        </Card>
    );
}
